import { useEffect, useId, useRef, useState } from 'react'
import GlassOverlay from './GlassOverlay'
import { applyExtractedProduct } from '../utils/applyExtractedProduct'
import { classifyImageUploadError } from '../utils/imageUploadErrors'
import { blurNumberOnWheel } from '../utils/numberInputWheel'
import { apiUrl } from '../utils/apiBase'
import './AddProductModal.css'

const DEFAULT_CERTAINTY = 'Faible'

const EMPTY_ITEM = {
  name: '',
  link: '',
  price: 0,
  quantity: 1,
  promo: '',
  certainty_label: DEFAULT_CERTAINTY,
  image_url: '',
  image_key: '',
}

function CloseGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

function ExtractGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M10 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-7" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <path d="M13 12H4m0 0 3.2-3.2M4 12l3.2 3.2" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CartPlusGlyph() {
  return (
    <span className="add-product-compound-icon">
      <img src="/Icon shopping cart.png" alt="" />
      <span className="add-product-plus-badge" aria-hidden="true">+</span>
    </span>
  )
}

function IconButton({
  type = 'button',
  icon,
  label,
  onClick,
  disabled = false,
  busy = false,
  motion = 'lift',
  primary = false,
  glyph = null,
  tooltip = true,
}) {
  return (
    <button
      type={type}
      className={`add-product-icon-btn is-${motion}${primary ? ' is-primary' : ''}${busy ? ' is-busy' : ''}${tooltip ? '' : ' is-plain'}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-busy={busy || undefined}
      title={tooltip ? label : undefined}
    >
      {glyph || <img src={icon} alt="" />}
    </button>
  )
}

export default function AddProductModal({
  isOpen,
  onClose,
  onSubmit,
  onExtract,
  onScanPhoto,
  isSubmitting = false,
  submitError = '',
  returnFocusRef,
  t = (key) => key,
}) {
  const titleId = useId()
  const hintId = useId()
  const urlInputRef = useRef(null)
  const imageFileRef = useRef(null)
  const dirtyRef = useRef(new Set())
  const extractingRef = useRef(false)
  const [item, setItem] = useState(EMPTY_ITEM)
  const [errors, setErrors] = useState({})
  const [extracting, setExtracting] = useState(false)
  const [pastingImage, setPastingImage] = useState(false)
  const [imageReady, setImageReady] = useState(false)
  const [localPreview, setLocalPreview] = useState('')

  useEffect(() => {
    if (!isOpen) return undefined
    setItem({ ...EMPTY_ITEM })
    setImageReady(false)
    setLocalPreview('')
    dirtyRef.current = new Set()
    setErrors({})
    return undefined
  }, [isOpen])

  const updateField = (key, value) => {
    if (extractingRef.current) dirtyRef.current.add(key)
    setItem((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: '' }))
  }

  const validate = () => {
    const next = {}
    if (!item.name.trim()) next.name = 'Indiquez un nom de produit.'
    if (item.link.trim()) {
      try {
        const parsed = new URL(item.link.trim())
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          next.link = 'L’URL doit commencer par http:// ou https://.'
        }
      } catch {
        next.link = 'URL invalide.'
      }
    }
    if (item.price < 0) next.price = 'Le prix ne peut pas être négatif.'
    if (!item.quantity || item.quantity < 1) next.quantity = 'La quantité doit être au moins 1.'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleExtract = async () => {
    const url = item.link.trim()
    if (!url) {
      setErrors((prev) => ({ ...prev, link: 'Entrez une URL pour extraire le produit.' }))
      return
    }
    dirtyRef.current = new Set()
    extractingRef.current = true
    setExtracting(true)
    setErrors((prev) => ({ ...prev, link: '' }))
    try {
      const product = await onExtract(url)
      setItem((prev) => applyExtractedProduct(prev, product, dirtyRef.current))
      if (product?.image_url && !dirtyRef.current.has('image_url')) {
        setImageReady(false)
      }
    } catch (error) {
      const message = error?.response?.data?.detail || error?.message || 'Impossible d’extraire les informations.'
      setErrors((prev) => ({ ...prev, link: message }))
    } finally {
      extractingRef.current = false
      setExtracting(false)
    }
  }

  const handlePasteImage = async () => {
    if (pastingImage || isSubmitting) return
    setPastingImage(true)
    setErrors((prev) => ({ ...prev, image_url: '' }))
    try {
      const clipboardItems = await navigator.clipboard.read()
      for (const clipboardItem of clipboardItems) {
        const imageType = clipboardItem.types.find((type) => type.startsWith('image/'))
        if (!imageType) continue
        const blob = await clipboardItem.getType(imageType)
        await uploadImageBlob(blob, 'pasted-image.png')
        setPastingImage(false)
        return
      }
      setErrors((prev) => ({ ...prev, image_url: 'Aucune image dans le presse-papier.' }))
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        image_url: classifyImageUploadError(error, { status: error?.status }),
      }))
    } finally {
      setPastingImage(false)
    }
  }

  const uploadImageBlob = async (blob, filename = 'product-image.jpg') => {
    const previewUrl = URL.createObjectURL(blob)
    setLocalPreview(previewUrl)
    try {
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' })
      const formData = new FormData()
      formData.append('file', file)
      const response = await fetch(apiUrl('/api/upload/image'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData,
      })
      if (!response.ok) {
        let detail = ''
        try {
          detail = (await response.json())?.detail || ''
        } catch {
          detail = ''
        }
        throw Object.assign(new Error(detail || 'Le transfert de l’image a échoué.'), { status: response.status })
      }
      const result = await response.json()
      if (!result?.url || !result?.key) {
        throw Object.assign(new Error('Le stockage n’a pas confirmé l’enregistrement.'), { status: 502 })
      }
      if (extractingRef.current) dirtyRef.current.add('image_url')
      setItem((prev) => ({ ...prev, image_url: result.url, image_key: result.key }))
      setImageReady(false)
      setLocalPreview('')
      URL.revokeObjectURL(previewUrl)
    } catch (error) {
      URL.revokeObjectURL(previewUrl)
      setLocalPreview('')
      throw error
    }
  }

  const handleImageFile = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || busy) return
    setPastingImage(true)
    setErrors((prev) => ({ ...prev, image_url: '' }))
    try {
      await uploadImageBlob(file, file.name || 'product-image.jpg')
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        image_url: classifyImageUploadError(error, { status: error?.status }),
      }))
    } finally {
      setPastingImage(false)
    }
  }

  const handleRemoveImage = () => {
    if (extractingRef.current) dirtyRef.current.add('image_url')
    setItem((prev) => ({ ...prev, image_url: '', image_key: '' }))
    setLocalPreview('')
    setImageReady(false)
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (isSubmitting || extracting) return
    if (!validate()) return
    onSubmit({ ...item, certainty_label: DEFAULT_CERTAINTY })
  }

  const busy = isSubmitting || extracting || pastingImage
  const requiredFieldsValid = Boolean(
    item.name.trim()
    && Number(item.price) >= 0
    && Number(item.quantity) >= 1,
  )

  return (
    <GlassOverlay
      isOpen={isOpen}
      onClose={onClose}
      allowClose={!busy}
      labelledBy={titleId}
      describedBy={hintId}
      panelClassName="add-product-dialog"
      initialFocusRef={urlInputRef}
      returnFocusRef={returnFocusRef}
    >
        <div className="add-product-content">
          <header className="add-product-header">
            <div>
              <h2 id={titleId}>{t('addProduct') || 'Ajouter un produit'}</h2>
              <p id={hintId} className="add-product-hint">
                Importez depuis une URL ou saisissez le produit manuellement.
              </p>
            </div>
            <div className="add-product-header-actions">
              {onScanPhoto ? (
                <button
                  type="button"
                  className="add-product-scan-entry"
                  onClick={onScanPhoto}
                  disabled={busy}
                >
                  <span aria-hidden="true">◉</span>
                  Ajouter par photo
                </button>
              ) : null}
              <IconButton
                label="Fermer"
                tooltip={false}
                onClick={onClose}
                disabled={busy}
                motion="spin"
                glyph={<CloseGlyph />}
              />
            </div>
          </header>

          <form className="add-product-form" onSubmit={handleSubmit} noValidate aria-busy={busy || undefined}>
            <div className="add-product-body">
              <div className={`add-product-field${errors.link ? ' has-error' : ''}`}>
                <label htmlFor="add-product-url">{t('linkUrl') || 'URL'}</label>
                <div className="add-product-url-row">
                  <input
                    id="add-product-url"
                    ref={urlInputRef}
                    type="url"
                    inputMode="url"
                    value={item.link}
                    onChange={(e) => updateField('link', e.target.value)}
                    placeholder="https://..."
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="add-product-import-btn"
                    onClick={handleExtract}
                    disabled={extracting || isSubmitting || !item.link.trim()}
                    aria-busy={extracting || undefined}
                    aria-label="Extraire depuis le lien"
                    title="Extraire depuis le lien"
                  >
                    <ExtractGlyph />
                    <span>{extracting ? 'Importation…' : 'Importer'}</span>
                  </button>
                </div>
                {errors.link ? <p className="add-product-error">{errors.link}</p> : null}
              </div>

              <div className={`add-product-field${errors.name ? ' has-error' : ''}`}>
                <label htmlFor="add-product-name">{t('productName') || 'Nom'} *</label>
                <input
                  id="add-product-name"
                  type="text"
                  value={item.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  required
                />
                {errors.name ? <p className="add-product-error">{errors.name}</p> : null}
              </div>

              <div className="add-product-row">
                <div className={`add-product-field${errors.price ? ' has-error' : ''}`}>
                  <label htmlFor="add-product-price">{t('priceEuro') || 'Prix (€)'}</label>
                  <input
                    id="add-product-price"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={item.price}
                    onChange={(e) => updateField('price', parseFloat(e.target.value) || 0)}
                    onWheel={blurNumberOnWheel}
                  />
                  {errors.price ? <p className="add-product-error">{errors.price}</p> : null}
                </div>
                <div className={`add-product-field${errors.quantity ? ' has-error' : ''}`}>
                  <label htmlFor="add-product-qty">{t('quantity') || 'Quantité'}</label>
                  <input
                    id="add-product-qty"
                    type="number"
                    inputMode="numeric"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateField('quantity', parseInt(e.target.value, 10) || 1)}
                    onWheel={blurNumberOnWheel}
                  />
                  {errors.quantity ? <p className="add-product-error">{errors.quantity}</p> : null}
                </div>
              </div>

              <div className={`add-product-field add-product-image-field${errors.image_url ? ' has-error' : ''}`}>
                <span className="add-product-label">{t('image') || 'Image'}</span>
                <div className="add-product-image-box">
                  <div className={`add-product-image-preview${(localPreview || (item.image_url && imageReady)) ? ' is-ready' : ''}${localPreview ? ' is-local' : ''}`}>
                    {localPreview || item.image_url ? (
                      <img
                        src={localPreview || item.image_url}
                        alt={localPreview ? 'Aperçu local, pas encore enregistré' : 'Aperçu du produit'}
                        onLoad={() => setImageReady(true)}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                          setErrors((prev) => ({
                            ...prev,
                            image_url: 'L’image n’a pas pu être chargée. Collez-en une autre ou retirez-la.',
                          }))
                        }}
                      />
                    ) : (
                      <img src="/Icon photo size select actual.png" alt="" className="add-product-image-empty" />
                    )}
                  </div>
                  <div className="add-product-image-actions">
                    <button
                      type="button"
                      className="add-product-image-action"
                      onClick={() => imageFileRef.current?.click()}
                      disabled={busy}
                    >
                      <span aria-hidden="true">↑</span>
                      Fichier
                    </button>
                    <input
                      ref={imageFileRef}
                      className="add-product-file-input"
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleImageFile}
                    />
                    <button
                      type="button"
                      className="add-product-image-action"
                      onClick={handlePasteImage}
                      disabled={busy}
                      aria-busy={pastingImage || undefined}
                    >
                      <img src="/Icon paste.png" alt="" />
                      {pastingImage ? 'Ajout…' : 'Coller'}
                    </button>
                    <button
                      type="button"
                      className="add-product-image-action is-danger"
                      onClick={handleRemoveImage}
                      disabled={busy || !item.image_url}
                    >
                      <img src="/Icon trash.png" alt="" />
                      Supprimer
                    </button>
                    <div className="add-product-image-url">
                      <label htmlFor="add-product-image-url">URL de l’image</label>
                      <input
                        id="add-product-image-url"
                        type="url"
                        value={item.image_url}
                        onChange={(e) => {
                          updateField('image_url', e.target.value)
                          setImageReady(false)
                        }}
                        placeholder="https://…"
                        inputMode="url"
                        autoCapitalize="none"
                        spellCheck={false}
                      />
                    </div>
                  </div>
                </div>
                {errors.image_url ? <p className="add-product-error">{errors.image_url}</p> : null}
              </div>
              {submitError ? <p className="add-product-error add-product-submit-error">{submitError}</p> : null}
            </div>

            <footer className="add-product-footer">
              <button
                type="button"
                className="add-product-footer-btn is-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="add-product-footer-btn is-primary"
                disabled={busy || !requiredFieldsValid}
                aria-busy={isSubmitting || undefined}
                aria-label={isSubmitting ? 'Ajout du produit en cours' : 'Ajouter le produit au panier'}
              >
                {isSubmitting ? <span className="add-product-submit-spinner" aria-hidden="true" /> : <CartPlusGlyph />}
                <span>{isSubmitting ? 'Ajout en cours…' : 'Ajouter au panier'}</span>
              </button>
            </footer>
          </form>
        </div>
    </GlassOverlay>
  )
}
